import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Eraser, PenLine, Type } from "lucide-react";

function typedToDataUrl(text) {
  const c = document.createElement("canvas");
  c.width = 640;
  c.height = 180;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.fillStyle = "#2B2F33";
  ctx.textBaseline = "middle";
  ctx.font = "72px 'Caveat', 'Dancing Script', cursive";
  ctx.fillText(text, 24, c.height / 2);
  return c.toDataURL("image/png");
}

export const SignaturePad = forwardRef(function SignaturePad({ fullName = "", onDirtyChange }, ref) {
  const [tab, setTab] = useState("draw");
  const [typed, setTyped] = useState("");
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    try {
      document.fonts.load("72px Caveat");
    } catch (e) {
      /* noop */
    }
  }, []);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width) return;
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#2B2F33";
  }, []);

  useEffect(() => {
    if (tab !== "draw") return;
    const t = setTimeout(() => {
      initCanvas();
      hasDrawnRef.current = false;
      onDirtyChange?.(false);
    }, 30);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (tab === "type") onDirtyChange?.(!!typed.trim());
  }, [tab, typed, onDirtyChange]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
    try {
      e.target.setPointerCapture?.(e.pointerId);
    } catch (err) {
      /* noop */
    }
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasDrawnRef.current) {
      hasDrawnRef.current = true;
      onDirtyChange?.(true);
    }
  };

  const end = () => {
    drawingRef.current = false;
  };

  const clearDraw = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    hasDrawnRef.current = false;
    onDirtyChange?.(false);
  };

  useImperativeHandle(
    ref,
    () => ({
      getSignature: () => {
        if (tab === "type") {
          return typed.trim() ? typedToDataUrl(typed.trim()) : "";
        }
        return hasDrawnRef.current ? canvasRef.current.toDataURL("image/png") : "";
      },
      isEmpty: () => (tab === "type" ? !typed.trim() : !hasDrawnRef.current),
    }),
    [tab, typed]
  );

  return (
    <div data-testid="signature-pad">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full grid grid-cols-2">
          <TabsTrigger value="draw" data-testid="signature-tab-draw">
            <PenLine style={{ width: 15, height: 15 }} className="mr-1.5" /> Disegna Firma
          </TabsTrigger>
          <TabsTrigger value="type" data-testid="signature-tab-type">
            <Type style={{ width: 15, height: 15 }} className="mr-1.5" /> Digita Firma
          </TabsTrigger>
        </TabsList>

        <TabsContent value="draw">
          <div className="relative rounded-xl border border-slate-300 bg-card overflow-hidden">
            <canvas
              ref={canvasRef}
              data-testid="signature-canvas"
              className="w-full h-40 touch-none cursor-crosshair block"
              style={{ touchAction: "none" }}
              onPointerDown={start}
              onPointerMove={move}
              onPointerUp={end}
              onPointerLeave={end}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
              <span className="text-[11px] text-slate-300 font-medium border-t border-dashed border-slate-200 px-10 pt-1">
                Firma qui sopra
              </span>
            </div>
          </div>
          <button
            type="button"
            data-testid="signature-clear-button"
            onClick={clearDraw}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-red-600 transition-colors"
          >
            <Eraser style={{ width: 13, height: 13 }} /> Cancella
          </button>
        </TabsContent>

        <TabsContent value="type">
          <Input
            data-testid="signature-type-input"
            placeholder="Digita il tuo nome"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            maxLength={40}
          />
          {fullName && !typed && (
            <button
              type="button"
              data-testid="signature-use-name-button"
              onClick={() => setTyped(fullName)}
              className="mt-2 text-xs font-medium text-primary hover:underline"
            >
              Usa "{fullName}"
            </button>
          )}
          <div className="mt-3 rounded-xl border border-slate-300 bg-card h-28 flex items-center px-6 overflow-hidden">
            <span
              data-testid="signature-type-preview"
              className="text-slate-900 truncate"
              style={{ fontFamily: "'Caveat', 'Dancing Script', cursive", fontSize: 48, lineHeight: 1 }}
            >
              {typed || <span className="text-slate-300">Anteprima firma</span>}
            </span>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
});
